import React from 'react';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close,
  Person,
  AccessTime,
  DateRange,
  Description,
  Work,
  Email,
  Phone,
  BusinessCenter,
  PictureAsPdf,
  GridOn // Import Excel icon
} from '@mui/icons-material';
import { exportDayAbsencesToPDF } from '../../utils/pdfExport';
import { exportDayAbsencesToExcel } from '../../utils/excelExport';
import ExportButtons from '../btns/ExportButtons';
import { IMAGE_BASE_URL } from '../../config';

const DayDetailsModal = ({
  open,
  onClose,
  selectedDate,
  dayAbsences = [],
  //employees = []
}) => {
  if (!selectedDate) return null;

  // Format date for display
  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const AbsenceTypeLabels = {
    LATENESS: 'Lateness',
    PARTIAL_DAY: 'Partial Day',
    FULL_DAY: 'Full Day',
    MULTIPLE_DAYS: 'Multiple Days'
  };

  const getAbsenceTypeColor = (type) => {
    const colorMap = {
      LATENESS: '#ff9800',
      PARTIAL_DAY: '#ffb74d',
      FULL_DAY: '#4caf50',
      MULTIPLE_DAYS: '#3f51b5'
    };
    return colorMap[type] || '#757575';
  };


  // Get absence type color and label
  const getAbsenceReasonInfo = (reason = '') => {
    const typeMap = {
      sick: { color: '#f44336', label: 'Sick Leave', icon: '🤒' },
      vacation: { color: '#4caf50', label: 'Vacation', icon: '🏖️' },
      personal: { color: '#2196f3', label: 'Personal Leave', icon: '👤' },
      late: { color: '#ff9800', label: 'Late Arrival', icon: '⏰' },
      early_departure: { color: '#ff5722', label: 'Early Departure', icon: '🚪' },
      meeting: { color: '#9c27b0', label: 'Meeting', icon: '🤝' },
      training: { color: '#607d8b', label: 'Training', icon: '📚' },
      emergency: {
        color: '#e53935',
        label: 'Emergency',
        icon: '🚨',
        keywords: ['emergency']
      },
      family: {
        color: '#ab47bc',
        label: 'Family Issue',
        icon: '👪',
        keywords: ['family', 'child', 'spouse', 'parent']
      },
      appointment: {
        color: '#00897b',
        label: 'Appointment',
        icon: '📅',
        keywords: ['appointment', 'doctor', 'medical', 'dentist']
      },
      traffic: {
        color: '#fdd835',
        label: 'Traffic Delay',
        icon: '🚗',
        keywords: ['traffic', 'jam', 'congestion']
      },
      transport: {
        color: '#5c6bc0',
        label: 'Transport Issue',
        icon: '🚌',
        keywords: ['bus', 'train', 'transport', 'commute', 'car']
      }
    };

    const lowerReason = reason.toLowerCase();

    for (const keyword in typeMap) {
      if (lowerReason.includes(keyword)) {
        return typeMap[keyword];
      }
    }

    return { color: '#757575', label: 'Other', icon: '📋' };
  };


  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timeString;
    }
  };

  // Calculate absence duration
  const calculateDuration = (startDate, endDate, startTime, endTime) => {
    const start = new Date(startDate);
    const end = new Date(endDate || startDate);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    if (daysDiff === 1 && startTime && endTime) {
      const startHour = parseInt(startTime.split(':')[0]);
      const endHour = parseInt(endTime.split(':')[0]);
      const hours = endHour - startHour;
      return `${hours} hour(s)`;
    }

    return daysDiff === 1 ? 'Full Day' : `${daysDiff} days`;
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusMap = {
      approved: '#4caf50',
      pending: '#ff9800',
      rejected: '#f44336',
      cancelled: '#757575'
    };
    return statusMap[status] || '#757575';
  };

  // Handle export to PDF
  const handleExportPDF = () => {
    exportDayAbsencesToPDF(selectedDate, dayAbsences);
  };

  // Handle export to Excel
  const handleExportExcel = () => {
    exportDayAbsencesToExcel(selectedDate, dayAbsences);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #7091E6 0%, #adbbda 50%)',
          color: 'white',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '8px',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DateRange sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
            {formatDisplayDate(selectedDate)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        padding: '24px',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {dayAbsences.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '32px',
              color: 'rgba(255,255,255,0.8)'
            }}
          >
            <Typography variant="h6">No absences on this day</Typography>
            <Typography variant="body2" sx={{ marginTop: '8px' }}>
              This day is clear of any scheduled absences.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ marginBottom: '16px', mt: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', marginBottom: '8px' }}>
                📊 Summary
              </Typography>
              <Paper
                sx={{
                  padding: '16px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px'
                }}
              >
                <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <Chip
                    icon={<Person />}
                    label={`${dayAbsences.length} Employee(s)`}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                  <Chip
                    icon={<AccessTime />}
                    label={`${dayAbsences.filter(abs => abs.type === 'late' || abs.type === 'early_departure').length} Partial Days`}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                  <Chip
                    icon={<BusinessCenter />}
                    label={`${dayAbsences.filter(abs => abs.type !== 'late' && abs.type !== 'early_departure').length} Full Days`}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                </Box>
              </Paper>
            </Box>

            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)', margin: '16px 0' }} />

            <Typography variant="h6" sx={{ color: 'white', marginBottom: '16px' }}>
              👥 Employee Details
            </Typography>

            <List sx={{ padding: 0 }}>
              {dayAbsences.map((absence, index) => {

                const absenceReasonInfo = getAbsenceReasonInfo(absence.reason);

                return (
                  <Paper
                    key={index}
                    sx={{
                      marginBottom: '16px',
                      padding: '16px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    <ListItem sx={{ padding: 0 }}>
                      <ListItemAvatar>
                        <Avatar
                          src={absence.employee.image ? `${IMAGE_BASE_URL}${absence.employee.image}` : undefined}
                          sx={{
                            width: 56,
                            height: 56,
                            backgroundColor: getAbsenceTypeColor(absence.type),
                            fontSize: '24px',
                            mr: 2,
                          }}
                        >
                          {absence.employee.image ? undefined : (absence.employee.name?.[0] || '?')}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <Typography variant="h4" sx={{ color: 'white' }}>
                              {absence.employee.firstName} {absence.employee.lastName}
                            </Typography>
                            <Chip
                              label={AbsenceTypeLabels[absence.type] || absence.type}
                              size="small"
                              sx={{
                                backgroundColor: getAbsenceTypeColor(absence.type),
                                color: 'white',
                                fontSize: '12px'
                              }}
                            />
                            <Chip
                              label={absenceReasonInfo.label}
                              size="small"
                              sx={{
                                backgroundColor: absenceReasonInfo.color,
                                color: 'white',
                                fontSize: '12px'
                              }}
                            />
                            {/* <Chip
                              label={absence.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(absence.status),
                                color: 'white',
                                fontSize: '12px'
                              }}
                            /> */}
                          </Box>
                        }
                        secondary={

                          <Box sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            <Box sx={{ display: 'flex', flexBasis: 'column', gap: 6, mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                                <Work sx={{ fontSize: '14px' }} />
                                <Typography variant="body2">
                                  {formatPositionsDisplay(absence.employee)} - {absence.employee.department?.name}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                                <Email sx={{ fontSize: '14px' }} />
                                <Typography variant="body2">{absence.employee.user.email}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                                <Phone sx={{ fontSize: '14px' }} />
                                <Typography variant="body2">{absence.employee.phone}</Typography>
                              </Box>
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', flexBasis: 'column', gap: 6 }}>
                              <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    Duration
                                  </Typography>
                                  <Typography variant="body2">
                                    {
                                      absence.hours
                                        ? `${absence.hours} hour(s)`
                                        : absence.type === 'PARTIAL_DAY'
                                          ? '4 hours'
                                          : calculateDuration(absence.date, absence.endDate, absence.startTime, absence.endTime)
                                    }

                                  </Typography>
                                </Box>
                              </Box>

                              {absence.reason && (
                                <Box sx={{ marginTop: '2px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Description sx={{ fontSize: '14px' }} />
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                      Reason
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    {absence.reason}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </Paper>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <ExportButtons
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          disabled={dayAbsences.length === 0}
        />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DayDetailsModal;